import { useEffect } from 'react';
import { MaturitySegment } from '@shared/interfaces/maturitySegment.interface.ts';

interface UseApiAuditParams {
  projectId?: string;
  maturityResults: { operationalBand: string; strategicBand: string } | null;
  maturitySegmentData?: MaturitySegment | null;
}

export function useApiAudit({ projectId, maturityResults, maturitySegmentData }: UseApiAuditParams) {
  useEffect(() => {
    const controller = new AbortController();
    const performApiAudit = async () => {
      if (!projectId || !maturityResults) return;

      const isHMR = (import.meta as any).hot != null;
      if (isHMR && document.visibilityState === 'hidden') return;

      console.log('🔗 API Endpoints Audit Starting...');

      const endpoints = [
        { name: 'maturity-segment', url: `/api/maturity-segment?operational=${maturityResults.operationalBand}&strategic=${maturityResults.strategicBand}` },
        { name: 'recommended-actions', url: maturitySegmentData?.id ? `/api/recommended-actions?segmentId=${maturitySegmentData.id}` : null },
        { name: 'question-insights', url: '/api/question-insights' },
        { name: 'strategic-themes', url: '/api/strategic-themes' }
      ];

      const token = localStorage.getItem('jwt_token');
      for (const endpoint of endpoints) {
        if (!endpoint.url) continue;
        try {
          console.log(`🔗 Testing endpoint: ${endpoint.name}`);
          const response = await fetch(endpoint.url, {
            credentials: 'same-origin',
            signal: controller.signal,
            headers: {
              'Accept': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            }
          });
          const contentType = response.headers.get('content-type');

          console.log(`🔗 ${endpoint.name}:`, {
            status: response.status,
            contentType: contentType,
            isJSON: contentType?.includes('application/json'),
            isHTML: contentType?.includes('text/html')
          });

          if (response.ok) {
            const data = await response.json();
            console.log(`🔗 ${endpoint.name} data:`, {
              type: Array.isArray(data) ? 'array' : typeof data,
              length: Array.isArray(data) ? data.length : Object.keys(data || {}).length,
              firstItem: Array.isArray(data) ? data[0] : data
            });
          } else {
            console.warn(`🔗 ${endpoint.name} failed:`, response.status, response.statusText);
          }
        } catch (error: any) {
          if (error?.name === 'AbortError' || error?.code === 'ABORT_ERR') {
            console.debug(`🔗 ${endpoint.name} audit aborted`);
            continue;
          }
          console.warn(`🔗 ${endpoint.name} network error:`, {
            message: error?.message,
            type: error?.name
          });
        }
      }
    };

    performApiAudit();
    return () => controller.abort();
  }, [projectId, maturityResults, maturitySegmentData]);
}
